import { FC } from 'react';
import { Stack, Text, Group } from '@mantine/core';
import { SlotInfo } from 'react-big-calendar';
import { format } from 'date-fns';
import { Modal } from 'src/components/Modal';
import { Button } from 'src/components/Button';
import { Form } from 'src/form';
import { InputText } from 'src/components/Form/Text';
import { InputDateTimePicker } from 'src/components/Form/DateTimePicker';
import { Select } from 'src/components/Form/Select';
import { useAddCampaignSlotModal } from './use-add-campaign-slot-modal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  slotInfo: SlotInfo;
  campaignId: number;
  minDate?: Date;
  maxDate?: Date;
}

export const AddCampaignSlotModal: FC<Props> = ({
  isOpen,
  onClose,
  slotInfo,
  campaignId,
  minDate,
  maxDate,
}) => {
  const {
    devices,
    interactions,
    brandings,
    isPending,
    handleSubmit,
    handleClose,
    validation,
  } = useAddCampaignSlotModal({
    campaignId,
    onClose,
  });

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Create Campaign Slot"
      size="lg"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
      withCloseButton={!isPending}
    >
      <Form
        initialValues={{
          name: '',
          dateFrom: slotInfo.start,
          dateTo: slotInfo.end,
          deviceId: '',
          interactionId: '',
          brandingId: '',
        }}
        validate={validation}
        onSubmit={handleSubmit}
        loading={isPending}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Slot: {format(slotInfo.start, 'EEEE, MMMM d')} -{' '}
            {format(slotInfo.end, 'EEEE, MMMM d')}
          </Text>

          <InputText
            name="name"
            label="Slot Name"
            placeholder="Enter slot name"
          />

          <Group grow>
            <InputDateTimePicker
              name="dateFrom"
              label="Start Date & Time"
              placeholder="Select start date and time"
              minDate={minDate}
              maxDate={maxDate}
            />
            <InputDateTimePicker
              name="dateTo"
              label="End Date & Time"
              placeholder="Select end date and time"
              minDate={minDate}
              maxDate={maxDate}
            />
          </Group>

          <Select
            name="deviceId"
            label="Device"
            placeholder="Select device"
            data={devices.map((device) => ({
              value: device.id.toString(),
              label: device.playwall_id,
            }))}
          />

          <Select
            name="interactionId"
            label="Interaction"
            placeholder="Select interaction"
            data={interactions.map((interaction) => ({
              value: interaction.id.toString(),
              label: `${interaction.name} (${interaction.interaction_dictionary?.name || 'No dictionary'})`,
            }))}
          />

          <Select
            name="brandingId"
            label="Branding"
            placeholder="Select branding"
            data={brandings.map((branding) => ({
              value: branding.id.toString(),
              label: branding.name,
            }))}
          />

          <Group justify="flex-end" mt="xl">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" loading={isPending}>
              Create Slot
            </Button>
          </Group>
        </Stack>
      </Form>
    </Modal>
  );
};
